/**
 * The old -> new titles under a rename change card: loads on open, filters by
 * folder, and puts back one file (or one folder's files) while the rest of
 * the change stays.
 */
import { AxiosError } from "axios";
import { ArrowRight, ChevronRight, Undo2 } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Spinner } from "@/components/ui/spinner";
import { AgentAPI } from "@/features/agents/api/agentApi";
import { useAttachmentScope } from "@/features/agents/attachments/AttachmentScopeContext";
import type { AgentChange, ChangeRenameRow } from "@/features/agents/types";
import { cn } from "@/lib/utils";

import { useCaseIdeOptional } from "../../context/CaseIdeContext";

const folderName = (path: string): string =>
  path === "/" ? "Case root" : path.split("/").filter(Boolean).pop() ?? path;

const errorText = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { error?: unknown } | undefined;
    if (typeof data?.error === "string") return data.error;
  }
  return error instanceof Error ? error.message : "Undo failed";
};

const FolderChip: FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  testId?: string;
}> = ({ label, active, onClick, testId }) => (
  <Button
    variant={active ? "secondary" : "ghost"}
    size="xs"
    className="h-5 px-1.5 text-xs font-normal"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
  >
    {label}
  </Button>
);

type Props = {
  change: AgentChange;
  onChange: (next: AgentChange) => void;
};

export const RenameTable: FC<Props> = ({ change, onChange }) => {
  const scope = useAttachmentScope();
  const ide = useCaseIdeOptional();
  const total = change.renamed_total ?? 0;
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ChangeRenameRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [folder, setFolder] = useState<string | null>(null);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [refused, setRefused] = useState<Record<string, string>>({});
  const applied = change.status === "applied";

  useEffect(() => {
    if (!open || rows || !scope || !change.thread_id) return undefined;
    let live = true;
    AgentAPI.changeRenames(scope, change.thread_id, change.id)
      .then((next) => live && setRows(next))
      .catch((error: unknown) => live && setLoadError(errorText(error)));
    return () => {
      live = false;
    };
  }, [open, rows, scope, change.thread_id, change.id]);

  const folders = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows ?? [])
      counts.set(row.folder, (counts.get(row.folder) ?? 0) + 1);
    return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  const isReverted = (row: ChangeRenameRow) =>
    row.reverted || change.status === "undone";
  const visible = (rows ?? []).filter(
    (row) => folder === null || row.folder === folder
  );
  const undoable = visible.filter((row) => !isReverted(row));

  const putBack = async (nodeIds: string[]) => {
    if (!scope || !change.thread_id || !nodeIds.length) return;
    setPending(new Set(nodeIds));
    try {
      const out = await AgentAPI.undoChangeTitles(
        scope,
        change.thread_id,
        change.id,
        nodeIds
      );
      const back = new Set(out.reverted);
      setRows((prev) =>
        (prev ?? []).map((row) =>
          back.has(row.node_id) ? { ...row, reverted: true } : row
        )
      );
      setRefused((prev) => ({
        ...prev,
        ...Object.fromEntries(out.refused.map((r) => [r.node_id, r.reason]))
      }));
      onChange({ ...change, ...out.change });
      if (back.size) {
        ide?.invalidate(["fs", "drafts", "packet"]);
        toast.success(
          back.size === 1 ? "Put back 1 title" : `Put back ${back.size} titles`
        );
      }
      if (out.refused.length && !back.size) toast.error(out.refused[0].reason);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setPending(new Set());
    }
  };

  if (!total) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className="flex items-center gap-2 text-sm text-fg-2 hover:text-fg"
        data-testid="rename-table-toggle"
      >
        <ChevronRight
          className={cn(
            "size-3.25 text-fg-4 transition-transform",
            open && "rotate-90"
          )}
        />
        {total} file{total === 1 ? "" : "s"} retitled
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 flex flex-col gap-1.5" data-testid="rename-table">
          {loadError ? (
            <div className="text-xs text-destructive">{loadError}</div>
          ) : rows === null ? (
            <Spinner className="size-3.5 text-muted-foreground" />
          ) : (
            <>
              {folders.length > 1 ? (
                <div className="flex flex-wrap gap-1">
                  <FolderChip
                    label={`All (${rows.length})`}
                    active={folder === null}
                    onClick={() => setFolder(null)}
                  />
                  {folders.map(([path, count]) => (
                    <FolderChip
                      key={path}
                      label={`${folderName(path)} (${count})`}
                      active={folder === path}
                      onClick={() => setFolder(path)}
                      testId={`rename-folder-${path}`}
                    />
                  ))}
                </div>
              ) : null}
              <ul className="max-h-64 overflow-y-auto rounded border border-line-2 bg-background">
                {visible.map((row) => {
                  const back = isReverted(row);
                  return (
                    <li
                      key={row.node_id}
                      data-testid={`rename-row-${row.node_id}`}
                      data-reverted={back ? "true" : "false"}
                      className="flex min-w-0 items-center gap-1.5 border-b border-line-1 px-2 py-1 text-xs last:border-b-0"
                    >
                      <span
                        className="min-w-0 max-w-[40%] truncate text-muted-foreground"
                        title={row.before}
                      >
                        {row.before}
                      </span>
                      <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
                      <span
                        className={cn(
                          "min-w-0 flex-1 truncate",
                          back
                            ? "text-muted-foreground line-through"
                            : "text-foreground"
                        )}
                        title={row.after}
                      >
                        {row.after}
                      </span>
                      {back ? (
                        <span className="shrink-0 text-muted-foreground">
                          Put back
                        </span>
                      ) : refused[row.node_id] ? (
                        <span
                          className="shrink-0 text-destructive"
                          title={refused[row.node_id]}
                        >
                          {refused[row.node_id]}
                        </span>
                      ) : applied ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          className="h-5 shrink-0 gap-1 px-1 text-xs text-muted-foreground hover:text-foreground"
                          disabled={pending.size > 0}
                          onClick={() => void putBack([row.node_id])}
                          data-testid={`undo-rename-${row.node_id}`}
                        >
                          <Undo2 className="size-3" /> Undo
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {folder !== null && applied && undoable.length > 1 ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className="h-6 self-start gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                  disabled={pending.size > 0}
                  onClick={() => void putBack(undoable.map((r) => r.node_id))}
                  data-testid="undo-rename-folder"
                >
                  <Undo2 className="size-3" /> Undo {undoable.length} in{" "}
                  {folderName(folder)}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
